import React, { JSX, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import {
  ArrowPathIcon,
  ArrowLeftStartOnRectangleIcon,
  CheckIcon,
  EnvelopeIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline'
import { LoadingSpinner } from 'components/Loading'
import { ErrorInline } from 'components/ErrorInline'
import { clearStoredSocketToken } from 'context/socketToken'
import { graphqlRequest, GENERIC_ERROR } from 'lib/helpers'
import resendVerificationEmailMutation from 'graphql/resendVerificationEmail.graphql'

export type VerifyGateState = 'unverified' | 'no-role'

interface ResendResult { sent: boolean }

export const VerifyGatePage = ({ state }: { state: VerifyGateState }): JSX.Element => {
  const { getAccessTokenSilently, logout } = useAuth0()

  const [resendBusy, setResendBusy] = useState(false)
  const [resendSent, setResendSent] = useState(false)
  const [resendError, setResendError] = useState<string | null>(null)
  const [checkBusy, setCheckBusy] = useState(false)

  const handleResend = async (): Promise<void> => {
    if (resendBusy) return
    setResendBusy(true)
    setResendError(null)
    setResendSent(false)
    try {
      const token = await getAccessTokenSilently()
      const { result } = await graphqlRequest<{ result: ResendResult }>(
        resendVerificationEmailMutation, {}, token
      )
      if (result.sent) {
        setResendSent(true)
      } else {
        setResendError(GENERIC_ERROR)
      }
    } catch (err) {
      setResendError(err instanceof Error && err.message !== '' ? err.message : GENERIC_ERROR)
    } finally {
      setResendBusy(false)
    }
  }

  const handleCheckAgain = async (): Promise<void> => {
    if (checkBusy) return
    setCheckBusy(true)
    try {
      await getAccessTokenSilently({ cacheMode: 'off' })
    } catch {
    }
    globalThis.location.reload()
  }

  const handleSignOut = (): void => {
    clearStoredSocketToken()
    void logout({ logoutParams: { returnTo: globalThis.location.origin } })
  }

  const isUnverified = state === 'unverified'

  return (
    <div
      data-testid='verify-gate-page'
      className='flex min-h-screen items-center justify-center bg-dm-bg-main px-4 text-dm-text-primary'
    >
      <div className='w-full max-w-md rounded-xl border border-white/5 bg-dm-bg-secondary p-8'>
        <div className='mb-5 flex flex-col items-center gap-3 text-center'>
          <div className='flex h-14 w-14 items-center justify-center rounded-full bg-brand/10 text-brand ring-1 ring-brand/20'>
            {isUnverified
              ? <EnvelopeIcon className='size-7' aria-hidden='true' />
              : <UserCircleIcon className='size-7' aria-hidden='true' />}
          </div>
          <h1 className='text-xl font-semibold text-dm-text-primary'>
            {isUnverified ? 'Verify your email' : 'Your account is being set up'}
          </h1>
          <p className='text-sm text-dm-text-secondary'>
            {isUnverified
              ? 'We sent a verification link to the email on your account. Follow it, then come back here. Verifying unlocks the app.'
              : 'Your email is verified and your account is being prepared. This usually only takes a moment. Check again shortly.'}
          </p>
        </div>

        <div className='flex flex-col gap-2'>
          {isUnverified && (
            <button
              type='button'
              onClick={() => { void handleResend() }}
              disabled={resendBusy}
              data-testid='verify-gate-resend'
              className='inline-flex items-center justify-center gap-2 rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
            >
              {resendBusy ? <LoadingSpinner /> : <EnvelopeIcon className='size-4' aria-hidden='true' />}
              {resendBusy ? 'Sending…' : 'Resend verification email'}
            </button>
          )}
          <button
            type='button'
            onClick={() => { void handleCheckAgain() }}
            disabled={checkBusy}
            data-testid='verify-gate-check-again'
            className='inline-flex items-center justify-center gap-2 rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            {checkBusy ? <LoadingSpinner /> : <ArrowPathIcon className='size-4' aria-hidden='true' />}
            {isUnverified ? "I've verified, check again" : 'Check again'}
          </button>
          <button
            type='button'
            onClick={handleSignOut}
            data-testid='verify-gate-sign-out'
            className='inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-dm-text-accent hover:text-dm-text-primary'
          >
            <ArrowLeftStartOnRectangleIcon className='size-4' aria-hidden='true' />
            Sign out
          </button>
        </div>

        {resendSent && (
          <div
            data-testid='verify-gate-resend-sent'
            className='mt-4 flex items-start gap-2 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-300'
          >
            <CheckIcon className='size-4 shrink-0' aria-hidden='true' />
            <span className='flex-1'>
              Verification email sent to the email on your account. The link
              in it unlocks the app.
            </span>
          </div>
        )}
        {resendError !== null && (
          <div className='mt-4' data-testid='verify-gate-resend-error'>
            <ErrorInline onDismiss={() => setResendError(null)}>
              {resendError}
            </ErrorInline>
          </div>
        )}
      </div>
    </div>
  )
}
